import {
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentIcon,
  EllipsisHorizontalIcon,
  PaperClipIcon,
  PencilIcon,
  PhotoIcon,
  PlusIcon,
  ReceiptPercentIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { AddPropertyButton, DateEditor, PropertyNameMenu, propertyTypeIcons, PropertyValue, PropertyValueView, ValueButton } from "@/components/properties/Properties";
import { SelectEditor } from "@/components/properties/SelectEditor";
import { Button, EmptyState, Field, Tag, UserChip } from "@/components/ui";
import { useFeedback } from "@/components/ui/feedback";
import { Modal } from "@/components/ui/Modal";
import { MenuItem, Popover } from "@/components/ui/Popover";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys } from "@/lib/queries";
import type { Expense, ExpenseAttachment, Finance, Property, Values } from "@/lib/types";
import { cn, formatBytes, formatDay, formatMoney, formatPercent, moneyInput, parseMoney, todayISO } from "@/lib/utils";
import { Money, MoneyInput, splitAmount, useFinanceRun } from "./common";

/** Property picker that edits local state; used before the expense exists. */
function DraftPropertyValue({ property, value, onChange }: { property: Property; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <Popover
      trigger={(t) => (
        <ValueButton ref={t.ref} onClick={t.onClick} empty={value.length === 0} className={cn("field min-h-[34px] px-2.5", t.open && "border-accent")}>
          <PropertyValueView property={property} value={value} />
        </ValueButton>
      )}
    >
      {(close) =>
        property.type === "date" ? (
          <DateEditor value={value[0]} onChange={(v) => onChange(v ? [v] : [])} onClose={close} />
        ) : (
          <SelectEditor
            propertyId={property.id}
            options={property.options}
            value={value}
            multi={property.type === "multi_select"}
            onChange={onChange}
            onClose={close}
            canManage
          />
        )
      }
    </Popover>
  );
}

/** One line on how an expense has been paid for. */
function expenseStatus(fin: Finance, e: Expense) {
  const money = (v: number) => formatMoney(v, fin.settings.currency);
  if (e.funding === "receipts") {
    return e.covered >= e.amount ? "Taken from payments" : e.covered > 0 ? `${money(e.amount - e.covered)} not yet taken` : "Not yet taken";
  }
  const debts = fin.debts.filter((d) => d.expenseId === e.id);
  const uncovered = debts.reduce((n, d) => n + (d.creditor === null ? d.outstanding : 0), 0);
  const between = debts.reduce((n, d) => n + (d.creditor !== null ? d.outstanding : 0), 0);
  if (uncovered > 0) return `${money(uncovered)} not covered yet`;
  if (between > 0) return `${money(between)} owed between members`;
  return "Paid by members";
}

const ACCEPT = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";

/** Client-side check matching the server: PDF, PNG or JPEG by extension. */
function acceptable(file: File) {
  return /\.(pdf|png|jpe?g)$/i.test(file.name);
}

function isPdf(mime: string, name: string) {
  return mime === "application/pdf" || name.toLowerCase().endsWith(".pdf");
}

/** Shows an expense's documents one at a time, with arrows between them. */
function AttachmentViewer({ attachments, start = 0, onClose }: { attachments: ExpenseAttachment[]; start?: number; onClose: () => void }) {
  const { pid } = useProject();
  const [index, setIndex] = useState(start);
  const a = attachments[Math.min(index, attachments.length - 1)];
  if (!a) return null;
  const url = api.expenseAttachmentUrl(pid, a.id);
  const many = attachments.length > 1;
  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={
        <span className="flex items-center gap-2" dir="auto">
          {a.name}
          {many && (
            <span className="text-sm font-normal text-muted">
              {index + 1} of {attachments.length}
            </span>
          )}
        </span>
      }
      footer={
        <>
          {many && (
            <div className="me-auto flex gap-1">
              <Button size="sm" icon={ChevronLeftIcon} disabled={index === 0} onClick={() => setIndex(index - 1)} aria-label="Previous document" />
              <Button size="sm" icon={ChevronRightIcon} disabled={index === attachments.length - 1} onClick={() => setIndex(index + 1)} aria-label="Next document" />
            </div>
          )}
          <a href={api.expenseAttachmentUrl(pid, a.id, true)} className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line px-3 text-sm font-medium hover:bg-hover">
            <ArrowDownTrayIcon className="size-4" /> Download
          </a>
        </>
      }
    >
      {isPdf(a.mimeType, a.name) ? (
        <iframe key={a.id} src={url} title={a.name} className="h-[70vh] w-full rounded-md border border-line bg-white" />
      ) : (
        <img key={a.id} src={url} alt={a.name} className="mx-auto max-h-[70vh] rounded-md" />
      )}
    </Modal>
  );
}

function DocRow({ name, size, pdf, note, onOpen, onRemove, removed, onUndo }: {
  name: string;
  size: number;
  pdf: boolean;
  note?: string;
  onOpen?: () => void;
  onRemove: () => void;
  removed?: boolean;
  onUndo?: () => void;
}) {
  const Icon = pdf ? DocumentIcon : PhotoIcon;
  return (
    <div className={cn("flex items-center gap-2 px-2.5 py-1.5 text-sm", removed && "opacity-60")}>
      <Icon className="size-4 shrink-0 text-muted" />
      {onOpen && !removed ? (
        <button type="button" onClick={onOpen} className="min-w-0 truncate text-start hover:underline" dir="auto">
          {name}
        </button>
      ) : (
        <span className={cn("min-w-0 truncate", removed && "line-through")} dir="auto">
          {name}
        </span>
      )}
      <span className="shrink-0 text-xs text-faint">{removed ? "will be removed" : note ?? formatBytes(size)}</span>
      {removed ? (
        <button type="button" onClick={onUndo} className="ms-auto shrink-0 rounded px-1.5 py-0.5 text-xs text-muted hover:bg-hover hover:text-fg">
          Keep
        </button>
      ) : (
        <button type="button" aria-label={`Remove ${name}`} onClick={onRemove} className="ms-auto shrink-0 rounded p-0.5 text-faint hover:bg-hover hover:text-danger">
          <XMarkIcon className="size-4" />
        </button>
      )}
    </div>
  );
}

/** Optional documents for the expense form: add several, drop them, preview or remove existing ones. */
function AttachmentsField({
  existing,
  removed,
  onToggleRemoved,
  files,
  onFiles,
  onOpen,
}: {
  existing: ExpenseAttachment[];
  removed: Set<string>;
  onToggleRemoved: (id: string) => void;
  files: File[];
  onFiles: (f: File[]) => void;
  onOpen: (index: number) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const add = (list: FileList | null) => {
    const picked = Array.from(list ?? []);
    const bad = picked.filter((f) => !acceptable(f));
    setError(bad.length ? `${bad.map((f) => f.name).join(", ")}: only PDF, PNG or JPG files can be attached` : "");
    const good = picked.filter(acceptable);
    if (good.length) onFiles([...files, ...good]);
  };
  const any = existing.length + files.length > 0;

  return (
    <div
      className="space-y-1"
      onDragOver={(e) => (e.preventDefault(), setDragging(true))}
      onDragLeave={(e) => e.currentTarget === e.target && setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        add(e.dataTransfer.files);
      }}
    >
      <span className="text-[13px] font-medium text-muted">
        Documents <span className="font-normal text-faint">(optional)</span>
      </span>
      <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={(e) => (add(e.target.files), (e.target.value = ""))} />
      <div className={cn("rounded-md border border-line", dragging && "border-accent bg-hover")}>
        {any && (
          <div className="divide-y divide-line border-b border-line">
            {existing.map((a, i) => (
              <DocRow
                key={a.id}
                name={a.name}
                size={a.sizeBytes}
                pdf={isPdf(a.mimeType, a.name)}
                onOpen={() => onOpen(i)}
                removed={removed.has(a.id)}
                onRemove={() => onToggleRemoved(a.id)}
                onUndo={() => onToggleRemoved(a.id)}
              />
            ))}
            {files.map((f, i) => (
              <DocRow
                key={`${f.name}-${i}`}
                name={f.name}
                size={f.size}
                pdf={isPdf(f.type, f.name)}
                note={`${formatBytes(f.size)} · new`}
                onRemove={() => onFiles(files.filter((_, j) => j !== i))}
              />
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-muted hover:bg-hover hover:text-fg"
        >
          <PaperClipIcon className="size-4" />
          {any ? "Attach more" : "Attach receipts or invoices"}
          <span className="ms-auto text-xs text-faint">PDF, PNG or JPG · drop files here</span>
        </button>
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

/** How a new amount would be split right now, and who can't pay their part from their available money. */
function SplitPreview({ fin, amount }: { fin: Finance; amount: number }) {
  const { userName, assignable } = useProject();
  const shares = fin.currentShares.filter((m) => m.share > 0);
  const members = shares.length ? shares : assignable.map((m) => ({ userId: m.id, share: 1 / assignable.length, weight: 0, tasks: 0 }));
  if (!members.length || amount <= 0) return null;
  const parts = splitAmount(amount, members);
  const available = new Map(fin.members.map((m) => [m.userId, m.available]));
  return (
    <div className="rounded-lg border border-line text-sm">
      <div className="border-b border-line px-3 py-1.5 text-[13px] text-muted">
        {shares.length ? "Charged to members by their current share" : "Nobody has finished work yet, so it's split equally"}
      </div>
      {members.map((m, i) => {
        const avail = available.get(m.userId) ?? 0;
        const short = parts[i] - Math.min(parts[i], avail);
        return (
          <div key={m.userId} className="flex items-center gap-3 px-3 py-1.5">
            <UserChip id={m.userId} name={userName(m.userId)} className="min-w-0 flex-1" />
            {short > 0 && <span className="text-xs text-danger">short {formatMoney(short, fin.settings.currency)}</span>}
            <span className="w-12 text-end text-muted tabular-nums">{formatPercent(m.share)}</span>
            <Money value={parts[i]} className="w-32 text-end" />
          </div>
        );
      })}
      {members.some((m, i) => parts[i] > (available.get(m.userId) ?? 0)) && (
        <div className="border-t border-line px-3 py-1.5 text-xs text-muted">
          Teammates with money available cover what someone can't pay, and get it back from that member's next payment.
        </div>
      )}
    </div>
  );
}

/** Who paid what for a balances-funded expense. */
function ExpenseBreakdown({ fin, expense }: { fin: Finance; expense: Expense }) {
  const { userName } = useProject();
  const debts = fin.debts.filter((d) => d.expenseId === expense.id);
  if (!expense.shares.length) return null;
  return (
    <div className="space-y-1">
      <span className="text-[13px] font-medium text-muted">Split between members</span>
      <div className="divide-y divide-line rounded-lg border border-line text-sm">
        {expense.shares.map((sh) => {
          const owes = debts.filter((d) => d.debtor === sh.userId && d.outstanding > 0);
          const coveredForOthers = debts.filter((d) => d.creditor === sh.userId).reduce((n, d) => n + d.amount, 0);
          return (
            <div key={sh.userId} className="px-3 py-1.5">
              <div className="flex items-center gap-3">
                <UserChip id={sh.userId} name={userName(sh.userId)} className="min-w-0 flex-1" />
                <span className="w-12 text-end text-muted tabular-nums">{formatPercent(sh.share)}</span>
                <Money value={sh.amount} className="w-32 text-end" />
              </div>
              {(owes.length > 0 || coveredForOthers > 0) && (
                <div className="mt-0.5 ps-7 text-xs text-muted">
                  {owes.map((d) => (
                    <div key={d.id} className="text-danger">
                      owes {formatMoney(d.outstanding, fin.settings.currency)} {d.creditor ? `to ${userName(d.creditor)}` : "(nobody could cover it)"}
                    </div>
                  ))}
                  {coveredForOthers > 0 && <div>covered {formatMoney(coveredForOthers, fin.settings.currency)} for teammates</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ExpenseForm({ fin, expense, onClose }: { fin: Finance; expense?: Expense; onClose: () => void }) {
  const { pid, propsFor } = useProject();
  const qc = useQueryClient();
  const run = useFinanceRun();
  const [title, setTitle] = useState(expense?.title ?? "");
  const [amount, setAmount] = useState(expense ? moneyInput(expense.amount) : "");
  const [spentOn, setSpentOn] = useState(expense?.spentOn ?? todayISO());
  const [note, setNote] = useState(expense?.note ?? "");
  const [values, setValues] = useState<Values>({});
  const [files, setFiles] = useState<File[]>([]);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [viewing, setViewing] = useState<number | null>(null);
  // set once the expense exists, so a failed upload can be retried without creating a duplicate
  const [savedId, setSavedId] = useState(expense?.id);
  const [saving, setSaving] = useState(false);
  const cents = parseMoney(amount);
  const properties = propsFor("expense");
  const valid = !!title.trim() && !!cents && !!spentOn;
  const existing = expense?.attachments ?? [];
  const givesBack = expense?.funding === "receipts" && cents !== null && cents < expense.covered ? expense.covered - cents : 0;
  const recharged = expense?.funding === "balances" && cents !== null && cents !== expense.amount;

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    const body = { title: title.trim(), amount: cents, spentOn, note };
    const ok = await run(
      (async () => {
        let id = savedId;
        if (id) {
          await api.updateExpense(pid, id, body);
        } else {
          const created = await api.createExpense(pid, body);
          id = created.id;
          setSavedId(id);
          await Promise.all(Object.entries(values).map(([propertyId, v]) => v.length && api.setValue(pid, created.id, propertyId, v)));
          qc.invalidateQueries({ queryKey: keys.project(pid) });
        }
        if (files.length) {
          await api.uploadExpenseAttachments(pid, id, files);
          setFiles([]);
        }
        for (const attachmentId of removed) await api.deleteExpenseAttachment(pid, attachmentId);
        setRemoved(new Set());
      })(),
    );
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={savedId ? "Edit expense" : "Record an expense"}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} disabled={!valid} onClick={submit}>
            {savedId ? "Save" : "Add expense"}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="Description">
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Server hosting for September" className="field" dir="auto" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Amount"
            error={amount && cents === null ? "Enter an amount like 120 or 120.50" : undefined}
            hint={
              givesBack > 0 ? (
                <>This frees <Money value={givesBack} layout="inline" /> already taken from received payments. It goes back to the team.</>
              ) : recharged ? (
                "Saving charges it again: members get back what they paid, then pay their parts of the new amount by today's shares."
              ) : undefined
            }
          >
            <MoneyInput value={amount} onChange={setAmount} currency={fin.settings.currency} invalid={!!amount && cents === null} />
          </Field>
          <Field label="Date">
            <input type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} className="field" />
          </Field>
        </div>
        {!savedId &&
          properties.map((p) => (
            <div key={p.id} className="space-y-1">
              <span className="text-[13px] font-medium text-muted">{p.name}</span>
              <DraftPropertyValue property={p} value={values[p.id] ?? []} onChange={(v) => setValues({ ...values, [p.id]: v })} />
            </div>
          ))}
        {(!expense || recharged) && cents ? <SplitPreview fin={fin} amount={cents} /> : expense && <ExpenseBreakdown fin={fin} expense={expense} />}
        <Field label="Note">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="field resize-none" placeholder="Vendor, invoice or receipt number…" dir="auto" />
        </Field>
        <AttachmentsField
          existing={existing}
          removed={removed}
          onToggleRemoved={(id) => {
            const next = new Set(removed);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            setRemoved(next);
          }}
          files={files}
          onFiles={setFiles}
          onOpen={setViewing}
        />
        <button type="submit" hidden />
      </form>
      {viewing !== null && <AttachmentViewer attachments={existing} start={viewing} onClose={() => setViewing(null)} />}
    </Modal>
  );
}

export function ExpensesTab({ fin }: { fin: Finance }) {
  const { pid, propsFor } = useProject();
  const run = useFinanceRun();
  const { confirm, toast } = useFeedback();
  const [editing, setEditing] = useState<Expense | "new" | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Expense | null>(null);
  const currency = fin.settings.currency;
  const properties = propsFor("expense");
  // the first select property (e.g. "Type") drives the breakdown and filter
  const typeProp = properties.find((p) => p.type === "select");

  const breakdown = useMemo(() => {
    if (!typeProp) return [];
    const sums = new Map<string, number>();
    for (const e of fin.expenses) {
      const key = e.values[typeProp.id]?.[0] ?? "";
      sums.set(key, (sums.get(key) ?? 0) + e.amount);
    }
    const byId = new Map(typeProp.options.map((o) => [o.id, o]));
    return [...sums.entries()]
      .map(([id, total]) => ({ id, total, option: byId.get(id) }))
      .sort((a, b) => b.total - a.total);
  }, [fin.expenses, typeProp]);

  const visible = typeProp && typeFilter !== null ? fin.expenses.filter((e) => (e.values[typeProp.id]?.[0] ?? "") === typeFilter) : fin.expenses;
  const total = fin.expenses.reduce((n, e) => n + e.amount, 0);
  const visibleTotal = visible.reduce((n, e) => n + e.amount, 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted">
          <Money value={total} layout="inline" /> spent across {fin.expenses.length} {fin.expenses.length === 1 ? "expense" : "expenses"}. Each new expense is charged to
          members' balances by their current share.
        </p>
        <Button variant="primary" icon={PlusIcon} onClick={() => setEditing("new")}>
          Add expense
        </Button>
      </div>

      {typeProp && breakdown.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setTypeFilter(null)}
            className={cn("h-7 rounded-full px-2.5 text-[13px]", typeFilter === null ? "bg-fg text-canvas" : "text-muted hover:bg-hover")}
          >
            All
          </button>
          {breakdown.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => setTypeFilter(typeFilter === b.id ? null : b.id)}
              className={cn("flex h-7 items-center gap-1.5 rounded-full border px-1 pe-2.5 text-[13px]", typeFilter === b.id ? "border-fg" : "border-line hover:bg-hover")}
            >
              {b.option ? <Tag color={b.option.color}>{b.option.label}</Tag> : <span className="ps-1.5 text-faint">No {typeProp.name.toLowerCase()}</span>}
              <Money value={b.total} layout="inline" className="text-muted" />
            </button>
          ))}
        </div>
      )}

      {fin.expenses.length === 0 ? (
        <EmptyState
          icon={ReceiptPercentIcon}
          title="No expenses yet"
          action={
            <Button variant="primary" icon={PlusIcon} onClick={() => setEditing("new")}>
              Record an expense
            </Button>
          }
        >
          Record what the project costs as it goes, such as hosting, licenses, hardware and contractors, so it's taken out before the team is paid.
        </EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line scroll-thin">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-start text-[13px] text-muted">
                <th className="w-32 px-3 py-1.5 text-start font-normal">Date</th>
                <th className="px-3 py-1.5 text-start font-normal">Description</th>
                {properties.map((p) => (
                  <th key={p.id} className="w-44 border-s border-line px-1 text-start font-normal">
                    <PropertyNameMenu
                      property={p}
                      content={
                        <>
                          {(() => {
                            const Icon = propertyTypeIcons[p.type];
                            return <Icon className="size-4 shrink-0 opacity-80" />;
                          })()}
                          <span className="truncate">{p.name}</span>
                        </>
                      }
                    />
                  </th>
                ))}
                <th className="w-36 border-s border-line px-3 py-1.5 text-end font-normal">Amount</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {visible.map((e) => (
                <tr key={e.id} className="group hover:bg-hover/50">
                  <td className="px-3 py-2 whitespace-nowrap text-muted">{formatDay(e.spentOn, true)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <button type="button" onClick={() => setEditing(e)} className="text-start hover:underline" dir="auto">
                        {e.title}
                      </button>
                      {e.attachments.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setViewing(e)}
                          title={e.attachments.map((a) => a.name).join("\n")}
                          aria-label={`View ${e.attachments.length === 1 ? "document" : `${e.attachments.length} documents`}`}
                          className="flex items-center gap-0.5 rounded px-0.5 text-xs text-muted hover:bg-hover hover:text-fg"
                        >
                          <PaperClipIcon className="size-3.5" />
                          {e.attachments.length > 1 && e.attachments.length}
                        </button>
                      )}
                    </div>
                    {e.note && (
                      <div className="line-clamp-1 text-xs text-muted" dir="auto">
                        {e.note}
                      </div>
                    )}
                  </td>
                  {properties.map((p) => (
                    <td key={p.id} className="border-s border-line p-0">
                      <PropertyValue property={p} entityId={e.id} values={e.values} variant="cell" />
                    </td>
                  ))}
                  <td className="border-s border-line px-3 py-2 text-end font-medium whitespace-nowrap">
                    <Money value={e.amount} />
                    <div className="text-[11px] font-normal text-faint">
                      {expenseStatus(fin, e)}
                    </div>
                  </td>
                  <td className="px-1">
                    <Popover
                      placement="bottom-end"
                      className="w-44 p-1"
                      trigger={(t) => (
                        <button ref={t.ref} onClick={t.onClick} type="button" className="rounded p-1 text-faint opacity-0 group-hover:opacity-100 hover:bg-hover hover:text-fg focus:opacity-100" aria-label="Expense actions">
                          <EllipsisHorizontalIcon className="size-4" />
                        </button>
                      )}
                    >
                      {(close) => (
                        <>
                          <MenuItem icon={PencilIcon} onClick={() => (close(), setEditing(e))}>
                            Edit
                          </MenuItem>
                          <MenuItem
                            icon={TrashIcon}
                            danger
                            onClick={async () => {
                              close();
                              const docs = e.attachments.length ? ` Its ${e.attachments.length === 1 ? "document is" : `${e.attachments.length} documents are`} deleted too.` : "";
                              const ok = await confirm({
                                title: `Delete "${e.title}"?`,
                                message:
                                  e.funding === "balances"
                                    ? `Each member gets back what they paid toward it, and the debts it created are cancelled.${docs}`
                                    : e.covered > 0
                                    ? `${formatMoney(e.covered, currency)} of it was already taken out of received payments. It goes back to the team, split the way those payments were split.${docs}`
                                    : `It hasn't been taken out of any payment yet, so no one's pay changes.${docs}`,
                                confirmLabel: "Delete",
                                danger: true,
                              });
                              if (!ok) return;
                              let returned = 0;
                              const done = await run(api.deleteExpense(pid, e.id).then((r) => void (returned = r.returnedToTeam)));
                              if (done && returned > 0) toast(`${formatMoney(returned, currency)} returned to the team`, "success");
                            }}
                          >
                            Delete
                          </MenuItem>
                        </>
                      )}
                    </Popover>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-line font-medium">
                <td className="px-3 py-2" colSpan={2 + properties.length}>
                  {typeFilter !== null ? "Filtered total" : "Total"}
                </td>
                <td className="border-s border-line px-3 py-2 text-end">
                  <Money value={visibleTotal} />
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <AddPropertyButton entityType="expense" />
      {viewing && <AttachmentViewer attachments={viewing.attachments} onClose={() => setViewing(null)} />}
      {editing && <ExpenseForm fin={fin} expense={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
